import { ChevronRight } from 'lucide-react';
import { SOUND_GROUPS } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DevicesTab/DevicesSoundTest/index.constants';
import type { DevicesSoundTestProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DevicesTab/DevicesSoundTest/index.types';
import {
  CARD_TITLE,
  SURFACE,
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import { AudioService } from 'Modules/softphone/services/AudioService';
import type { AudioType } from 'Modules/softphone/services/AudioService';
import React, { useCallback, useState } from 'react';
import { cn } from '~/lib/utils';

/**
 * DevicesSoundTest - Play each softphone sound, to hear where it comes out
 *
 * Grouped and collapsible.
 */
export const DevicesSoundTest: React.FC<DevicesSoundTestProps> = ({
  audioPlaying,
}) => {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      SOUND_GROUPS.map((group) => [group.name, group.defaultOpen])
    )
  );

  const handleToggleAudio = useCallback(
    async (type: AudioType, isPlaying: boolean) => {
      try {
        const audioService = AudioService.getInstance();

        if (isPlaying) {
          await audioService.stop(type);
        } else {
          await audioService.play(type);
        }
      } catch (error) {
        console.error(`Failed to toggle ${type}:`, error);
      }
    },
    []
  );

  const playingNow = SOUND_GROUPS.flatMap((group) => group.types).filter(
    (type) => audioPlaying[type]
  );

  return (
    <div className={SURFACE.card}>
      <div className={cn('flex items-center gap-2', SURFACE.cardHeader)}>
        <span className={CARD_TITLE}>Sound test</span>
        <div className="flex-1" />
        {playingNow.length > 0 && (
          <span className="flex items-center gap-1.5 font-sp-mono text-[10.5px] text-emerald-400">
            <span className="h-[5px] w-[5px] animate-pulse rounded-full bg-emerald-400" />
            playing {playingNow.join(', ')}
          </span>
        )}
      </div>

      {SOUND_GROUPS.map((group) => {
        const isOpen = Boolean(openGroups[group.name]);
        const playingInGroup = group.types.filter(
          (type) => audioPlaying[type]
        ).length;

        return (
          <div key={group.name} className="border-b bg-zinc-800 border-zinc-800 last:border-b-0">
            <button
              type="button"
              onClick={() =>
                setOpenGroups((current) => ({
                  ...current,
                  [group.name]: !current[group.name],
                }))
              }
              aria-expanded={isOpen}
              className="flex w-full items-center gap-2 bg-zinc-900 px-3 py-2 text-left transition-colors hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-400/40"
            >
              <ChevronRight
                className={cn(
                  'h-3 w-3 flex-shrink-0 text-zinc-400 transition-transform',
                  isOpen && 'rotate-90'
                )}
              />
              <span className="flex-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-zinc-300">
                {group.name}
              </span>
              {playingInGroup > 0 && !isOpen && (
                <span className="h-[5px] w-[5px] animate-pulse rounded-full bg-emerald-400" />
              )}
              <span className="font-sp-mono text-[10.5px] text-zinc-400">
                {group.types.length}
              </span>
            </button>

            {isOpen && (
              <div className="grid grid-cols-2 gap-[5px] p-2">
                {group.types.map((type) => {
                  const isPlaying = audioPlaying[type];
                  const tone = isPlaying ? TONE.ok : TONE.muted;

                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => void handleToggleAudio(type, isPlaying)}
                      title={
                        isPlaying ? `Stop ${type} sound` : `Play ${type} sound`
                      }
                      className={cn(
                        'flex items-center gap-[7px] px-[9px] py-[7px] transition-colors',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40',
                        tone.inset,
                        isPlaying ? tone.text : 'text-zinc-300'
                      )}
                    >
                      <span
                        className={cn(
                          'h-[5px] w-[5px] flex-shrink-0 rounded-full',
                          tone.dot,
                          isPlaying && 'animate-pulse'
                        )}
                      />
                      <span className="min-w-0 flex-1 truncate text-left font-sp-mono text-[11px]">
                        {type}
                      </span>
                      <span className="flex-shrink-0 text-[9px]">
                        {isPlaying ? '❚❚' : '▶'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
